import type { OptionHandle } from "building-types/handles/docEntityHandleTypes";
import type { DocOptionRule } from "building-types/options/docOptionRule";
import type { Formula } from "building-types/options/formula";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { DocOptionEffect } from "building-types/options/optionEffect";
import type { DocOptionSet } from "building-types/options/optionSet";
import type { RWTable, Table } from "common/data/table";
import { sortTableBySortIndex } from "common/utils/sortIndex";
import type { Value } from "expr-eval";
import { compileFormula, formulaOptionHandles } from "../formula/formulaConditions";
import { isFormula, parseFormula } from "../formula/formulaParsing";
import { formulaInvert } from "../formula/formulaUtils";

export function optionRuleNewProps(
  optionSets: Table<DocOptionSet>,
  optionHandle: OptionHandle,
): Partial<DocOptionRule> {
  const optionSet = optionSets[optionHandle.optionSetId];
  const option = optionSet.options[optionHandle.optionKey];
  if (!(optionSet && option)) {
    console.assert(false);
  }

  const ruleName = `${optionSet.name} > ${option.name} Rule`;
  const formula = formulaInvert({
    type: "FORMULA",
    formula: compileFormula({
      conditionGroups: {},
      typeDefs: optionSets,
      conditions: [
        {
          typeDefId: optionSet.id,
          value: optionHandle.optionKey,
          operator: "==",
        },
      ],
    }),
  }) as Formula;

  return {
    name: ruleName,
    predicate: formula ?? null,
  };
}

export function getRulesForOption(
  optionRules: Table<DocOptionRule>,
  optionEffects: Table<DocOptionEffect>,
  optionHandle: OptionHandle,
): { triggerRules: Table<DocOptionRule>; effectRules: Table<DocOptionRule> } {
  const triggerRules: RWTable<DocOptionRule> = {};
  const effectRules: RWTable<DocOptionRule> = {};
  for (const [id, rule] of Object.entries(optionRules)) {
    // gather all rules where option is referenced in formulas
    if (!isFormula(rule.predicate)) {
      continue;
    }

    for (const [optionSetId, optionIds] of Object.entries(formulaOptionHandles(rule.predicate.formula))) {
      if (optionIds.includes(optionHandle.optionKey) && optionHandle.optionSetId === optionSetId) {
        triggerRules[id] = rule;
      }
    }

    for (const { optionSetId, optionId, parentId } of Object.values(optionEffects)) {
      if (parentId !== rule.id) {
        continue;
      }

      if (optionHandle.optionSetId === optionSetId && optionHandle.optionKey === optionId) {
        effectRules[id] = rule;
      }
    }
  }
  return { triggerRules, effectRules };
}

/**
 * Provides a map for looking up Option Rules for given optionSetId and optionKey
 * @param optionRules complete list of Option Rules
 * @param optionEffects complete list of Option Effects
 * @returns
 */

export interface OptionRuleReferencesByOptionSet {
  [optionSetId: string]: OptionRuleReferencesByOption;
}

export interface OptionRuleReferencesByOption {
  [optionKey: string]: {
    [ruleId: string]: {
      name: string;
      type: "EFFECT" | "RULE";
      invalidReference?: { optionSetId: string; optionKey: string };
    };
  };
}

export type OptionRuleRef = OptionRuleReferencesByOption["string"]["string"];

export function getRulesByOptions(
  optionRules: Table<DocOptionRule>,
  optionEffects: Table<DocOptionEffect>,
  optionSets: Table<DocOptionSet>,
): OptionRuleReferencesByOptionSet {
  const rulesMap: OptionRuleReferencesByOptionSet = {};
  const filteredRules: RWTable<DocOptionRule> = {};
  for (const [ruleId, rule] of Object.entries(optionRules)) {
    if (!rule.predicate) {
      continue;
    }

    if (!isFormula(rule.predicate)) {
      continue;
    }

    if (rule.predicate.formula === undefined) {
      continue;
    }

    filteredRules[ruleId] = rule;
  }

  for (const [ruleId, rule] of Object.entries(filteredRules)) {
    // gather all rules where option is referenced in formulas

    // find invalid references
    let invalidReference;
    const triggerOptions = formulaOptionHandles(rule.predicate.formula);
    for (const [optionSetId, optionKeys] of Object.entries(triggerOptions)) {
      for (const optionKey of optionKeys) {
        if (optionSets[optionSetId]?.options?.[optionKey] === undefined) {
          invalidReference = {
            optionSetId,
            optionKey,
          };
        }
      }
    }

    // add rule to options referenced in trigger predicate
    for (const [optionSetId, optionKeys] of Object.entries(formulaOptionHandles(rule.predicate.formula))) {
      // check if option references are contained in the provided optionSet

      const existingOptionSet = rulesMap[optionSetId] ?? {};

      for (const optionKey of optionKeys) {
        rulesMap[optionSetId] = {
          ...existingOptionSet,
          [optionKey]: {
            ...(existingOptionSet[optionKey] ?? {}),
            [ruleId]: {
              name: rule.name,
              type: "RULE",
              invalidReference,
            },
          },
        };
      }
    }
  }

  for (const { optionSetId, optionId, parentId, effectType } of Object.values(optionEffects)) {
    // skip effects without specified options
    if (optionId === undefined) {
      continue;
    }

    // skip orphaned effects
    if (filteredRules[parentId] === undefined) {
      continue;
    }

    // only include effects that are ENABLE
    const existingOptionSet = rulesMap[optionSetId] ?? {};
    if (effectType !== "ENABLE") {
      continue;
    }

    const rule = filteredRules[parentId];

    if (optionId) {
      rulesMap[optionSetId] = {
        ...existingOptionSet,
        [optionId]: {
          ...(existingOptionSet[optionId] ?? {}),
          [parentId]: {
            name: rule.name,
            type: "EFFECT",
          },
        },
      };
    }
  }
  return rulesMap;
}

export function getRuleChoosingSelectedOption({
  optionId,
  optionSetId,
  optionRules,
  optionEffects,
  optionChoicesWithDefaults,
}: {
  optionId: string;
  optionSetId: string;
  optionRules: Table<DocOptionRule>;
  optionEffects: Table<DocOptionEffect>;
  optionChoicesWithDefaults: OptionChoices;
}): DocOptionRule | null {
  const h: OptionHandle = { optionSetId, optionKey: optionId, type: "OPTION" };

  for (const rule of sortTableBySortIndex(getRulesForOption(optionRules, optionEffects, h).effectRules)) {
    try {
      if (!parseFormula(rule.predicate.formula).evaluate(optionChoicesWithDefaults as { [key: string]: Value })) {
        return rule;
      }
    } catch (e) {}
  }

  return null;
}
